import { DndContext, useDraggable } from '@dnd-kit/core';
import type { ExpandedState } from '@tanstack/react-table';
import { cn } from '@tempo-io/sui-class-utils';
import { addDays, differenceInDays, format } from 'date-fns';
import debounce from 'lodash.debounce';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Resizable, type ResizeCallbackData } from 'react-resizable';
import { useUpsertTimelineLayoutMutation } from '@/lib/mutations/loop-visualization/useTimelineLayoutMutations';
import { DateFormat, toLocalMidnight } from '@/lib/utils/common/dateFormats';
import { TIMELINE_ROW_AXIS_LABEL_WIDTH } from '../layout/TimelineRowAxisLabel';
import { ITEM_LANE_HEIGHT, ITEM_LANE_HEIGHT_COMPACT } from '../TimelineItem';
import { PARENT_HEADER_HEIGHT } from '../useTimelineColumnDayWidth';
import type { TimelineRowType } from '../useTimelineTableColumns';
import { getNormalizePlannedDates } from '../utils/timelineDateUtils';
import type { ItemWithStackingOrder } from '../utils/timelineStackingUtils';

const TIMELINE_MINIMAP_HEIGHT = 60;
const MINIMAP_MIN_SCALE_RATIO = 0.1;
const MINIMAP_MAX_SCALE_RATIO = 0.13;
const THROTTLE_MS = 500;
const THROTTLE_PIXELS = 60;

const ResizeHandle = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { handleAxis?: string }
>(({ handleAxis, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={`react-resizable-handle react-resizable-handle-${handleAxis} absolute h-full cursor-ew-resize bg-bg-neutral-bold/80 transition-all pointer-events-auto`}
      style={{
        touchAction: 'none',
        width: '6px',
        ...(handleAxis === 'w' ? { left: 0 } : handleAxis === 'e' ? { right: 0 } : {}),
      }}
      {...props}
    />
  );
});

ResizeHandle.displayName = 'TimelineMinimapResizeHandle';

const DraggableFrameContainer = ({
  children,
  scopeOffset,
  scopeWidth,
  onTransformChange,
  maxWidth,
  canEdit,
}: {
  children: React.ReactNode;
  scopeOffset: number;
  scopeWidth: number;
  onTransformChange: (translateX: number) => void;
  maxWidth: number;
  canEdit: boolean;
}) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: 'timeline-minimap-draggable',
    disabled: !canEdit,
  });

  const rawTranslateX = transform?.x || 0;
  const minTranslateX = -scopeOffset;
  const maxTranslateX = maxWidth - scopeOffset - scopeWidth;
  const clampedTranslateX = Math.max(minTranslateX, Math.min(maxTranslateX, rawTranslateX));

  useEffect(() => {
    onTransformChange(clampedTranslateX);
  }, [clampedTranslateX, onTransformChange]);

  return (
    <div
      ref={setNodeRef}
      className="absolute top-0"
      style={{
        left: scopeOffset + clampedTranslateX,
        width: scopeWidth,
        height: TIMELINE_MINIMAP_HEIGHT - 2, // accounts for top and bottom border
      }}
    >
      <div
        {...listeners}
        {...attributes}
        className={cn(
          'absolute inset-0 inset-ring-bg-neutral-bold pointer-events-auto cursor-grab border-2 border-bg-neutral-bold/80 active:cursor-grabbing',
          { 'cursor-default': !canEdit },
        )}
      />
      {children}
    </div>
  );
};

const COMPACT_MODE_ROW_GAP = 10;

export const TimelineMinimap = ({
  visualizationId,
  viewId,
  data,
  minimapStart,
  minimapEnd,
  from,
  to,
  canEdit,
  onChange,
  scrollContainerRef,
  expandedRows,
  itemColorById,
  isCompact,
}: {
  visualizationId: number;
  viewId: number;
  data: TimelineRowType[];
  minimapStart: string;
  minimapEnd: string;
  from: string;
  to: string;
  canEdit: boolean;
  onChange: (dateRange: { from: string; to: string }) => void;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  expandedRows: ExpandedState;
  itemColorById?: Record<number, string>;
  isCompact: boolean;
}) => {
  const laneHeight = isCompact ? ITEM_LANE_HEIGHT_COMPACT + COMPACT_MODE_ROW_GAP : ITEM_LANE_HEIGHT;
  const minimapRef = useRef<HTMLDivElement | null>(null);
  const dragStartWidthRef = useRef<number>(0);
  const dragStartOffsetRef = useRef<number>(0);
  const dragStartFromRef = useRef<string>('');
  const dragStartToRef = useRef<string>('');
  const lastUpdateTimeRef = useRef<number>(0);
  const lastUpdateOffsetRef = useRef<number>(0);
  const lastUpdateWidthRef = useRef<number>(0);

  const { mutate: mutateLayout } = useUpsertTimelineLayoutMutation(viewId, visualizationId);

  const totalDaysInView = differenceInDays(minimapEnd, minimapStart) + 1;
  const totalDaysInScope = differenceInDays(to, from) + 1;
  const totalDaysFromItemStartToScopeStart = differenceInDays(from, minimapStart);
  const [width, setWidth] = useState(0);
  const [topScroll, setTopScroll] = useState(0);
  const calculatedScopeWidth = (width * totalDaysInScope) / totalDaysInView || width;
  const calculatedScopeOffset = (width * totalDaysFromItemStartToScopeStart) / totalDaysInView;

  const flattenedRowsAndSubRows = useMemo(() => {
    const rowsAndSubRows = data.flatMap((row) => {
      if (
        (expandedRows === true || expandedRows[row.id]) &&
        row.subRows &&
        row.subRows.length > 0
      ) {
        return [row, ...row.subRows];
      }
      return [row];
    });
    return rowsAndSubRows;
  }, [data, expandedRows]);

  const totalContentHeight = useMemo(() => {
    let contentHeight = 0;

    for (const row of flattenedRowsAndSubRows) {
      if (row.subRows) {
        contentHeight += PARENT_HEADER_HEIGHT;
      } else {
        const lanes = row.maxOrder ? row.maxOrder + 1 : 1;
        contentHeight += lanes * laneHeight;
      }
    }
    return contentHeight;
  }, [flattenedRowsAndSubRows, laneHeight]);

  const dynamicScaleRatio = useMemo(() => {
    if (totalContentHeight === 0) return MINIMAP_MIN_SCALE_RATIO;

    const targetScale = TIMELINE_MINIMAP_HEIGHT / totalContentHeight;

    return Math.min(Math.max(targetScale, MINIMAP_MIN_SCALE_RATIO), MINIMAP_MAX_SCALE_RATIO);
  }, [totalContentHeight]);

  const [interactionState, setInteractionState] = useState<{
    width: number;
    leftOffset: number;
    previewFrom?: string;
    previewTo?: string;
    isDragging?: boolean;
  } | null>(null);

  const [dragTransformX, setDragTransformX] = useState(0);

  const scopeWidth = interactionState ? interactionState.width : calculatedScopeWidth;
  const scopeOffset = interactionState ? interactionState.leftOffset : calculatedScopeOffset;

  const setScrollTopDebounced = useCallback(
    debounce((value: number) => {
      setTopScroll(value);
    }, 100),
    [],
  );

  useEffect(() => {
    if (!minimapRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setWidth(entry.contentRect.width);
      }
    });

    observer.observe(minimapRef.current);

    return () => observer.disconnect();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scrollContainerRef is a stable ref; setScrollTopDebounced is created once via lodash.debounce
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const onScroll = () => {
      setScrollTopDebounced(el.scrollTop);
    };
    el.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      el.removeEventListener('scroll', onScroll);
      setScrollTopDebounced.cancel();
    };
  }, []);

  const rowsToRender = useMemo(() => {
    const rows = [];
    let accumulatedHeight = 0;

    for (const row of flattenedRowsAndSubRows) {
      if (row.subRows) {
        continue;
      }
      const rowHeight = (row.maxOrder ? row.maxOrder + 1 : 1) * laneHeight * dynamicScaleRatio;

      if (accumulatedHeight + rowHeight < topScroll * dynamicScaleRatio) {
        accumulatedHeight += rowHeight;
        continue;
      }
      if (
        accumulatedHeight + laneHeight * dynamicScaleRatio >
        topScroll * dynamicScaleRatio + TIMELINE_MINIMAP_HEIGHT
      ) {
        break;
      }
      accumulatedHeight += rowHeight;
      rows.push(row);
    }
    return rows;
  }, [flattenedRowsAndSubRows, topScroll, dynamicScaleRatio, laneHeight]);

  const onResizeStart = (_e: React.SyntheticEvent, { size }: ResizeCallbackData) => {
    dragStartWidthRef.current = calculatedScopeWidth;
    dragStartOffsetRef.current = calculatedScopeOffset;
    dragStartFromRef.current = from;
    dragStartToRef.current = to;
    lastUpdateTimeRef.current = Date.now();
    lastUpdateOffsetRef.current = calculatedScopeOffset;
    lastUpdateWidthRef.current = size.width;
    setInteractionState({
      width: size.width,
      leftOffset: calculatedScopeOffset,
    });
  };

  const onResize = (_e: React.SyntheticEvent, { handle, size }: ResizeCallbackData) => {
    const pixelPerDay = width / totalDaysInView;

    if (handle === 'w') {
      const totalWidthChange = dragStartWidthRef.current - size.width;
      let newLeftOffset = dragStartOffsetRef.current + totalWidthChange;

      if (newLeftOffset < 0) {
        const clampedWidth = size.width + newLeftOffset;
        newLeftOffset = 0;

        setInteractionState({
          width: clampedWidth,
          leftOffset: newLeftOffset,
          previewFrom: minimapStart,
          previewTo: dragStartToRef.current,
        });
      } else {
        const offsetChangeFromLastUpdate = newLeftOffset - lastUpdateOffsetRef.current;
        const daysChangeFromLastUpdate = Math.round(offsetChangeFromLastUpdate / pixelPerDay);
        const calculatedPreviewFrom = addDays(
          new Date(dragStartFromRef.current),
          daysChangeFromLastUpdate,
        );
        const earliestDate = new Date(minimapStart);
        const latestAllowedFrom = new Date(dragStartToRef.current);
        const clampedFrom =
          calculatedPreviewFrom < earliestDate
            ? earliestDate
            : calculatedPreviewFrom > latestAllowedFrom
              ? latestAllowedFrom
              : calculatedPreviewFrom;
        const previewFrom = format(clampedFrom, 'yyyy-MM-dd');

        setInteractionState({
          width: size.width,
          leftOffset: newLeftOffset,
          previewFrom,
          previewTo: dragStartToRef.current,
        });

        const now = Date.now();
        if (
          now - lastUpdateTimeRef.current >= THROTTLE_MS &&
          Math.abs(offsetChangeFromLastUpdate) >= THROTTLE_PIXELS
        ) {
          onChange({ from: previewFrom, to: dragStartToRef.current });
          dragStartFromRef.current = previewFrom;
          lastUpdateTimeRef.current = now;
          lastUpdateOffsetRef.current = newLeftOffset;
        }
      }
    } else if (handle === 'e') {
      const currentLeftOffset = interactionState?.leftOffset ?? calculatedScopeOffset;
      let newWidth = size.width;

      if (currentLeftOffset + newWidth > width) {
        newWidth = width - currentLeftOffset;
        const previewTo = minimapEnd;

        setInteractionState((prev) => ({
          width: newWidth,
          leftOffset: prev?.leftOffset ?? calculatedScopeOffset,
          previewFrom: dragStartFromRef.current,
          previewTo,
        }));
      } else {
        const totalWidthChange = size.width - dragStartWidthRef.current;
        const daysChange = Math.round(totalWidthChange / pixelPerDay);
        const calculatedPreviewTo = addDays(new Date(dragStartToRef.current), daysChange);
        const latestDate = new Date(minimapEnd);
        const earliestAllowedTo = new Date(dragStartFromRef.current);
        const clampedTo =
          calculatedPreviewTo > latestDate
            ? latestDate
            : calculatedPreviewTo < earliestAllowedTo
              ? earliestAllowedTo
              : calculatedPreviewTo;
        const previewTo = format(clampedTo, 'yyyy-MM-dd');

        setInteractionState((prev) => ({
          width: size.width,
          leftOffset: prev?.leftOffset ?? calculatedScopeOffset,
          previewFrom: dragStartFromRef.current,
          previewTo,
        }));
        const now = Date.now();
        const widthChange = Math.abs(size.width - lastUpdateWidthRef.current);
        if (now - lastUpdateTimeRef.current >= THROTTLE_MS && widthChange >= THROTTLE_PIXELS) {
          onChange({ from: dragStartFromRef.current, to: previewTo });
          dragStartToRef.current = previewTo;
          dragStartWidthRef.current = size.width;
          lastUpdateTimeRef.current = now;
          lastUpdateWidthRef.current = size.width;
        }
      }
    }
  };

  const onResizeStop = () => {
    if (interactionState?.previewFrom && interactionState?.previewTo) {
      const finalFrom =
        interactionState.previewFrom > interactionState.previewTo
          ? interactionState.previewTo
          : interactionState.previewFrom;
      const finalTo =
        interactionState.previewTo < interactionState.previewFrom
          ? interactionState.previewFrom
          : interactionState.previewTo;
      const previousDates = { from, to };
      onChange({ from: finalFrom, to: finalTo });
      mutateLayout(
        {
          startDate: finalFrom,
          endDate: finalTo,
        },
        {
          onError: () => {
            onChange(previousDates);
          },
        },
      );

      setInteractionState(null);
    }
  };

  const handleDragStart = () => {
    dragStartFromRef.current = from;
    dragStartToRef.current = to;
  };

  const handleDragMove = (event: { delta: { x: number } }) => {
    if (interactionState && !interactionState.isDragging) return;

    const deltaX = event.delta.x;
    const pixelPerDay = width / totalDaysInView;

    const minDeltaX = -calculatedScopeOffset;
    const maxDeltaX = width - calculatedScopeOffset - calculatedScopeWidth;
    const clampedDeltaX = Math.max(minDeltaX, Math.min(maxDeltaX, deltaX));

    const daysChange = Math.round(clampedDeltaX / pixelPerDay);

    const currentFrom = new Date(dragStartFromRef.current || from);
    const currentTo = new Date(dragStartToRef.current || to);

    const newFrom = addDays(currentFrom, daysChange);
    const newTo = addDays(currentTo, daysChange);

    setInteractionState({
      width: calculatedScopeWidth,
      leftOffset: calculatedScopeOffset,
      previewFrom: format(newFrom, 'yyyy-MM-dd'),
      previewTo: format(newTo, 'yyyy-MM-dd'),
      isDragging: true,
    });
  };

  const handleDragEnd = () => {
    setDragTransformX(0);

    if (
      interactionState?.previewFrom &&
      interactionState?.previewTo &&
      interactionState.isDragging
    ) {
      onChange({
        from: interactionState.previewFrom,
        to: interactionState.previewTo,
      });
      const previousDates = { from, to };
      mutateLayout(
        {
          startDate: interactionState.previewFrom,
          endDate: interactionState.previewTo,
        },
        {
          onError: () => {
            onChange(previousDates);
          },
        },
      );
    }

    setTimeout(() => {
      setInteractionState(null);
    }, 100);
  };

  const paddingBuffer = 8;
  const minimapWidth = `calc(100% - ${TIMELINE_ROW_AXIS_LABEL_WIDTH + paddingBuffer}px)`;

  const fromDate = toLocalMidnight(from);
  const toDate = toLocalMidnight(to);
  const previewFromDate = interactionState?.previewFrom
    ? toLocalMidnight(interactionState.previewFrom)
    : null;
  const previewToDate = interactionState?.previewTo
    ? toLocalMidnight(interactionState.previewTo)
    : null;

  return (
    <DndContext onDragStart={handleDragStart} onDragMove={handleDragMove} onDragEnd={handleDragEnd}>
      <div
        ref={minimapRef}
        className="flex flex-col relative shadow-sm rounded-sm border border-border-neutral-bold bg-bg-surface-base ml-2 pt-1 mb-1"
        style={{
          height: TIMELINE_MINIMAP_HEIGHT,
          minHeight: TIMELINE_MINIMAP_HEIGHT,
          width: minimapWidth,
          maxWidth: minimapWidth,
          marginLeft: TIMELINE_ROW_AXIS_LABEL_WIDTH,
        }}
      >
        <div className="absolute inset-0 overflow-hidden">
          {rowsToRender.map((row) => {
            const rowHeight =
              (row.maxOrder ? row.maxOrder + 1 : 1) * laneHeight * dynamicScaleRatio;
            return (
              <div
                key={row.id}
                className="relative"
                style={{ height: rowHeight, minHeight: rowHeight, flex: '1 0 auto' }}
              >
                {Object.values(row.items || {}).map((weekItems) =>
                  weekItems.map((item) => (
                    <MinimapItem
                      key={`${row.id}-item-${item.id}`}
                      item={item}
                      headerColor={itemColorById?.[item.id] ?? row.headerColor}
                      minimapStart={minimapStart}
                      totalDaysInView={totalDaysInView}
                      minimapWidth={width}
                      scaleRatio={dynamicScaleRatio}
                      laneHeight={laneHeight}
                      isCompact={isCompact}
                    />
                  )),
                )}
              </div>
            );
          })}
        </div>

        <div
          className="absolute top-0 left-0 h-full bg-bg-brand-subtle/50 pointer-events-none"
          style={{ width: scopeOffset }}
        />

        <div
          className="absolute top-0 h-full bg-bg-brand-subtle/50 pointer-events-none"
          style={{ left: scopeOffset + scopeWidth, width: width - (scopeOffset + scopeWidth) }}
        />

        <DraggableFrameContainer
          scopeOffset={scopeOffset}
          scopeWidth={scopeWidth}
          onTransformChange={setDragTransformX}
          maxWidth={width}
          canEdit={canEdit}
        >
          <Resizable
            width={scopeWidth}
            height={TIMELINE_MINIMAP_HEIGHT}
            resizeHandles={canEdit ? ['w', 'e'] : []}
            handle={<ResizeHandle />}
            onResizeStart={onResizeStart}
            onResize={onResize}
            onResizeStop={onResizeStop}
            axis="x"
            minConstraints={[10, TIMELINE_MINIMAP_HEIGHT]}
            maxConstraints={[width, TIMELINE_MINIMAP_HEIGHT]}
          >
            <div className="w-full h-full" />
          </Resizable>
        </DraggableFrameContainer>

        {/* Date range label when resizing or dragging */}
        {interactionState && (
          <div
            className="absolute z-50 bottom-0 transform -translate-x-1/2 translate-y-full mt-2 bg-bg-neutral-bold text-text-neutral-inverted px-3 py-1.5 rounded text-sm whitespace-nowrap pointer-events-none shadow-lg"
            style={{ left: scopeOffset + dragTransformX + scopeWidth / 2 }}
          >
            {previewFromDate
              ? format(previewFromDate, DateFormat.MONTH_SHORT_DAY_SHORT_YEAR)
              : fromDate
                ? format(fromDate, DateFormat.MONTH_SHORT_DAY_SHORT_YEAR)
                : ''}{' '}
            -{' '}
            {previewToDate
              ? format(previewToDate, DateFormat.MONTH_SHORT_DAY_SHORT_YEAR)
              : toDate
                ? format(toDate, DateFormat.MONTH_SHORT_DAY_SHORT_YEAR)
                : ''}
          </div>
        )}
      </div>
    </DndContext>
  );
};

const MinimapItem = ({
  item,
  minimapStart,
  headerColor,
  totalDaysInView,
  minimapWidth,
  scaleRatio,
  laneHeight,
  isCompact,
}: {
  item: ItemWithStackingOrder;
  minimapStart: string;
  headerColor?: string;
  totalDaysInView: number;
  minimapWidth: number;
  scaleRatio: number;
  laneHeight: number;
  isCompact: boolean;
}) => {
  const normalizedDates = getNormalizePlannedDates(
    item.plannedStartDateTime,
    item.plannedFinishDateTime,
  );
  if (!normalizedDates.hasAnyDate || !normalizedDates.start || !normalizedDates.finish) {
    return null;
  }

  const itemStartOffsetDays = differenceInDays(normalizedDates.start, minimapStart);
  const itemDurationDays = differenceInDays(normalizedDates.finish, normalizedDates.start) + 1;

  const left = (itemStartOffsetDays / totalDaysInView) * minimapWidth;
  const width = (itemDurationDays / totalDaysInView) * minimapWidth || 0;
  const top = (item.order || 0) * laneHeight * scaleRatio;

  return (
    <div
      className={cn('absolute bg-bg-info', {
        'rounded-sm': !isCompact,
      })}
      style={{
        top,
        left,
        width,
        backgroundColor: headerColor,
        height: laneHeight * scaleRatio - 1,
      }}
    />
  );
};
